<template>
  <PageContainer maxWidth="1200px">
    <div class="grid">
      <div class="col-12 sm:col-4">
        <router-link
          :to="{
            name: ROUTE_HOST_CAMPAIGNS,
          }"
        >
          <Button
            class="text-sm p-0"
            icon="pi pi-arrow-left"
            link
            label="Back to campaigns"
          />
        </router-link>
      </div>

      <div class="col-12 sm:col-8">
        <template v-if="approvalButtonsAreVisible">
          <div class="flex justify-content-end flex-wrap">
            <Button
              class="mb-2 ml-2 sm:ml-0"
              text
              label="Decline"
              severity="secondary"
              @click="disapproveDialogIsVisible = true"
            />
            <Button
              class="mb-2 ml-2"
              label="Accept Request"
              @click="approveDialogIsVisible = true"
            />
          </div>
          <!-- Disable feature for pilot -->
          <div class="flex justify-content-end">
            <p class="mb-0 mt-2 pt-2 border-top-1 border-gray-300 text-right">
              <small>
                Don't like the generated ad? <br />
                <!-- <Button
                  class="text-sm p-0"
                  label="Replace"
                  link
                  @click="uploadAdDialogIsVisible = true"
                />
                it with your own version<br />
                or -->
                You may
                <Button
                  class="text-sm p-0"
                  label="Regenerate"
                  link
                  @click="regenerateAdDialogIsVisible = true"
                />
                the ad.
              </small>
            </p>
          </div>
        </template>

        <div class="flex justify-content-end flex-wrap gap-2">
          <Button
            v-if="showManualDetailsButton"
            label="View Campaign Details"
            icon="pi pi-copy"
            outlined
            @click="manualDetailsDialogIsVisible = true"
          />
          <Button
            v-if="showPauseAdButton"
            label="Pause Ad"
            outlined
            :loading="isPauseSubmitting"
            @click="isPauseDialogVisible = true"
          />
          <Button
            v-if="showResumeAdButton"
            label="Resume Ad"
            outlined
            :loading="isResumeSubmitting"
            @click="isResumeDialogVisible = true"
          />
        </div>
      </div>

      <div
        v-if="approvalButtonsAreVisible"
        class="col-12"
      >
        <WarningMessage>
          If you do not approve before the campaign start date,
          you will not be able to participate in the campaign.
        </WarningMessage>
      </div>

      <div
        v-if="isManualProvider && approvalButtonsAreVisible"
        class="col-12"
      >
        <Message
          severity="info"
          icon="pi pi-info-circle"
          :closable="false"
        >
          As a manual provider, after approving this ad you will be shown
          the campaign details and VAST tag to configure in your own ad
          serving system.
        </Message>
      </div>
    </div>

    <!-- TODO - add loading state -->
    <!-- TODO - add error state? -->

    <template v-if="compiledAd">
      <!-- header -->
      <div class="grid line-height-3 mt-3">
        <div class="col-12 md:col">
          <span class="text-sm text-gray-400">
            {{ compiledAd.org_name || '-' }}
          </span><br />
          <span class="text-lg font-bold">
            {{ compiledAd.campaign_name || '-' }}
          </span><br />
          <Chip
            v-if="statusChip"
            :class="[statusChip.class, 'mt-2 text-xs']"
            :label="statusChip.value"
            v-tooltip.top="'Status'"
          />
        </div>

        <div class="col-6 md:col">
          <span class="text-sm text-gray-400">
            Duration
          </span><br />
          <span class="text-lg font-bold">
            {{ compiledAd.durationInDays }}
            day{{ compiledAd.durationInDays === 1 ? '' : 's' }}
          </span>
        </div>

        <div class="col-6 md:col">
          <span class="text-sm text-gray-400">
            Ends {{ compiledAd.endDateDisplay }}
          </span><br />
          <span class="text-lg font-bold">
            {{ compiledAd.durationSubtext }}
          </span>
        </div>

        <div class="col-6 md:col">
          <span class="text-sm text-gray-400">
            Podcast
          </span><br />
          <span class="text-lg font-bold">
            {{ compiledAd.program_name }}
          </span>
        </div>
      </div>

      <Card
        class="mt-3 bg-gray-100"
        :pt="{
          body: {
            class: 'p-2'
          }
        }"
      >
        <template #content>
          <div class="flex">
            <div class="flex flex-grow-1 px-2">
              <audio
                class="w-full"
                controls
                :src="compiledAd.s3_signed_url"
              />
            </div>
            <!-- <Button
              class="text-sm"
              label="Transcribe"
              text
            /> -->
          </div>
        </template>
      </Card>

      <div class="flex">
        <h2>
          Campaign Details
        </h2>
      </div>
      <CampaignImpressionsCard
        :isLoading="analyticsStore.adImpressionsAreLoading[compiledAd.id] || false"
        :impressions="this.analyticsStore.adImpressions[this.$route.params.adId] || []"
        @exportImpressions="onExportImpressions"
      />

      <div class="mt-4">
        <h2>
          General Information
        </h2>
        <ul class="information-list">
          <li>
            <span class="text-sm text-gray-500">Target gender</span><br />
            <Chip class="bg-white px-3 py-2 font-bold border-gray-200 mt-2 border-1">
              {{ compiledAd.genderLabel }}
            </Chip>
          </li>
          <li>
            <span class="text-sm text-gray-500">Ages</span><br />
            <Chip
              v-for="item in compiledAd.targetAges"
              :key="item"
              class="bg-white px-3 py-2 font-bold border-gray-200 mt-2 border-1"
            >
              {{ item }}
            </Chip>
          </li>
          <li v-if="compiledAd.targetCategoryLabels.length > 0">
            <span class="text-sm text-gray-500">Categories</span><br />
            <Chip
              v-for="item in compiledAd.targetCategoryLabels"
              :key="item"
              class="bg-white px-3 py-2 font-bold border-gray-200 mt-2 border-1"
            >
              {{ item }}
            </Chip>
          </li>
          <li>
            <span class="text-sm text-gray-500">Ad run slot</span><br/>
            <Chip class="bg-white px-3 py-2 font-bold border-gray-200 mt-2 border-1">
              {{ compiledAd.ad_run_slot }}
            </Chip>
          </li>
        </ul>
      </div>
    </template>

    <ConfirmDialog
      v-model:visible="approveDialogIsVisible"
      header="Confirm Request"
      @confirm="onApproveAd"
      :isSubmitting="adApprovalIsSubmitting"
    >
      <p>
        Are you sure you want to accept this campaign request?
      </p>
      <p>
        Once approved, your podcast will begin running the ad.
      </p>
    </ConfirmDialog>

    <PromptDialog
      v-model:visible="disapproveDialogIsVisible"
      header="Decline Request"
      @confirm="onDisapproveAd"
      :isSubmitting="adDisapprovalIsSubmitting"
    >
      <p>
        Are you sure you want to decline this campaign request?
      </p>
      <p>
        Please provide a reason below
      </p>
    </PromptDialog>

    <UploadAdDialog
      v-model:visible="uploadAdDialogIsVisible"
      :isSubmitting="isUploadAdSubmitting"
      :adScript="compiledAd ? compiledAd.generated_ad_script : null"
      @submit="onSubmitUploadAd"
    />

    <ConfirmDialog
      v-model:visible="isPauseDialogVisible"
      :isSubmitting="isPauseSubmitting"
      header="Pause Ad"
      @confirm="onSubmitPauseAd"
    >
      <p>
        Are you sure you want to pause the ad for
        <strong>
          {{ compiledAd
            ? compiledAd.campaign_name
            : '-'
          }}
        </strong>
        ?
      </p>
    </ConfirmDialog>

    <ConfirmDialog
      v-model:visible="isResumeDialogVisible"
      :isSubmitting="isResumeSubmitting"
      header="Resume Ad"
      @confirm="onSubmitResumeAd"
    >
      <p>
        Are you sure you want to resume the ad for
        <strong>
          {{ compiledAd
            ? compiledAd.campaign_name
            : '-'
          }}
        </strong>
        ?
      </p>
    </ConfirmDialog>

    <ConfirmDialog
      v-model:visible="regenerateAdDialogIsVisible"
      header="Regenerate Ad"
      :confirmButtonDisabled="remainingRegenerateCount === 0"
      :isSubmitting="isRegenerateAdSubmitting"
      @confirm="onSubmitRegenerateAd"
    >
      <p>
        You may regenerate the ad up to 3 times.
        Regenerating the ad can help with pronounciation issues.
      </p>
      <p>
        You have {{ remainingRegenerateCount }}
        attempt{{ remainingRegenerateCount === 1 ? '' : 's'  }} remaining.
      </p>
    </ConfirmDialog>

    <ManualCampaignDetailsDialog
      v-model:visible="manualDetailsDialogIsVisible"
      :ad="compiledAd"
    />
  </PageContainer>
</template>

<script>
import { DateTime } from 'luxon';
import { mapStores } from 'pinia';
import { ROUTE_HOST_CAMPAIGNS } from '@/router/routes';
import {
  useAdsStore,
  useCategoriesStore,
  useMyUserStore,
  useAnalyticsStore,
} from '@/stores';
import { parseMessageFromError } from '@/utils/errors';
import { isJsonString, exportToCsv } from '@/utils/helpers';
import {
  AD_STATUS_PAUSED,
  AD_STATUS_RUNNING,
  GENDER_OPTIONS,
  CHART_INTERVAL_DAILY,
  AD_REGENERATE_ATTEMPT_MAX,
  CAMPAIGN_STATUS_PAUSED,
  HOSTING_PLATFORM_MANUAL,
} from '@/constants';
import ConfirmDialog from '@/components/confirmDialog';
import PromptDialog from '@/components/promptDialog';
import * as api from '@/api';
import { getAdStatusChip } from '@/utils/statuses';
import UploadAdDialog from '@/components/uploadAdDialog';
import CampaignImpressionsCard from '@/components/campaignImpressionsCard';
import ManualCampaignDetailsDialog from '@/components/manualCampaignDetailsDialog';
import { generateAdRegenerateAttemptCountKey } from '@/utils/localStorageKeys';

export default {
  components: {
    ConfirmDialog,
    PromptDialog,
    UploadAdDialog,
    CampaignImpressionsCard,
    ManualCampaignDetailsDialog,
  },
  computed: {
    ...mapStores(useMyUserStore, useAdsStore, useCategoriesStore, useAnalyticsStore),
    compiledAd() {
      const ad = this.adsStore.getAdById(this.$route.params.adId);

      if (!ad) return null;

      const durationInDays = Math.ceil(
        DateTime.fromISO(ad.end_date)
          .diff(DateTime.fromISO(ad.start_date), ['days']).days,
      );

      const endDateDisplay = DateTime.fromISO(ad.end_date)
        .toLocaleString(DateTime.DATE_SHORT);

      const campaignHasStarted = DateTime.fromISO(ad.start_date).diffNow('days').days < 0;
      let durationSubtext = '';

      if (campaignHasStarted) {
        const daysLeft = Math.max(
          Math.ceil(DateTime.fromISO(ad.end_date).diffNow('days').days),
          0,
        );

        durationSubtext = `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`;
      } else {
        const daysUntilStart = Math.ceil(DateTime.fromISO(ad.start_date).diffNow('days').days);

        durationSubtext = `${daysUntilStart} day${daysUntilStart === '1' ? '' : 's'} until start`;
      }

      const targetAges = isJsonString(ad.target_ages)
        ? Object.keys(JSON.parse(ad.target_ages))
        : [];

      const targetCategories = isJsonString(ad.target_categories)
        ? JSON.parse(ad.target_categories)
        : [];

      const targetCategoryLabels = targetCategories.reduce((acc, item) => {
        const matchingCategory = this.categoriesStore.categoryDictionary[Number(item)];

        if (matchingCategory) acc.push(matchingCategory.name);

        return acc;
      }, []);

      const genderLabel = GENDER_OPTIONS.some((item) => item.value === ad.target_gender)
        ? GENDER_OPTIONS.find((item) => item.value === ad.target_gender).label
        : ad.target_gender;

      return {
        ...ad,
        durationInDays,
        endDateDisplay,
        durationSubtext,
        targetAges,
        targetCategoryLabels,
        genderLabel,
      };
    },
    approvalButtonsAreVisible() {
      const ad = this.adsStore.getAdById(this.$route.params.adId);

      if (!ad || !ad.generated_ad_id) return false;

      return ad.approved === 0 && ad.disapproved_reason === null;
    },
    isManualProvider() {
      return this.compiledAd && this.compiledAd.hosting_platform === HOSTING_PLATFORM_MANUAL;
    },
    showManualDetailsButton() {
      return this.isManualProvider
        && this.compiledAd
        && this.compiledAd.approved === 1
        && this.compiledAd.generated_ad_id;
    },
    showPauseAdButton() {
      return this.compiledAd && this.compiledAd.status === AD_STATUS_RUNNING;
    },
    showResumeAdButton() {
      return this.compiledAd
      && this.compiledAd.status === AD_STATUS_PAUSED
      && this.compiledAd.campaign_status !== CAMPAIGN_STATUS_PAUSED;
    },
    statusChip() {
      if (!this.compiledAd) return null;

      return getAdStatusChip(this.compiledAd.status, this.compiledAd.campaign_status);
    },
    remainingRegenerateCount() {
      if (!this.compiledAd) {
        return AD_REGENERATE_ATTEMPT_MAX;
      }

      const key = generateAdRegenerateAttemptCountKey(this.compiledAd.id);
      const currentAttemptCount = localStorage.getItem(key);

      if (currentAttemptCount === null || Number.isNaN(Number(currentAttemptCount))) {
        return AD_REGENERATE_ATTEMPT_MAX;
      }

      return Math.max(0, AD_REGENERATE_ATTEMPT_MAX - Number(currentAttemptCount));
    },
  },
  data() {
    return {
      ROUTE_HOST_CAMPAIGNS,
      HOSTING_PLATFORM_MANUAL,
      approveDialogIsVisible: false,
      adApprovalIsSubmitting: false,
      disapproveDialogIsVisible: false,
      adDisapprovalIsSubmitting: false,
      uploadAdDialogIsVisible: false,
      isUploadAdSubmitting: false,
      isPauseDialogVisible: false,
      isPauseSubmitting: false,
      isResumeDialogVisible: false,
      isResumeSubmitting: false,
      regenerateAdDialogIsVisible: false,
      isRegenerateAdSubmitting: false,
      manualDetailsDialogIsVisible: false,
    };
  },
  mounted() {
    this.getAd();
  },
  methods: {
    async getAd() {
      try {
        await this.adsStore.getAds({
          adId: this.$route.params.adId,
        });

        const ad = this.adsStore.getAdById(this.$route.params.adId);

        // add 2 weeks to ad end date because TAP campaigns
        // may go longer than scheduled end date
        const adEndDate = DateTime.fromISO(ad.end_date).plus({ weeks: 2 }).toFormat('yyyy-MM-dd');

        await this.analyticsStore.getAdImpressions({
          adId: ad.id,
          startDate: ad.start_date,
          endDate: adEndDate,
        });
      } catch (error) {
        const message = parseMessageFromError(error, 'Error loading campaign.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      }
    },
    async onApproveAd() {
      try {
        this.adApprovalIsSubmitting = true;

        await api.setApprovalGeneratedAd({
          generatedAdId: this.compiledAd.generated_ad_id,
          approved: 1,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully approved campaign.',
        });

        this.approveDialogIsVisible = false;

        await this.getAd();

        if (this.isManualProvider) {
          this.manualDetailsDialogIsVisible = true;
        }
      } catch (error) {
        const message = parseMessageFromError(error, 'Error approving campaign');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.adApprovalIsSubmitting = false;
      }
    },
    async onDisapproveAd(input) {
      try {
        this.adDisapprovalIsSubmitting = true;

        await api.setApprovalGeneratedAd({
          generatedAdId: this.compiledAd.generated_ad_id,
          approved: 0,
          disapprovedReason: input,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully declined campaign.',
        });

        this.disapproveDialogIsVisible = false;

        this.getAd();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error declining campaign');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.adDisapprovalIsSubmitting = false;
      }
    },
    async onSubmitUploadAd(adFile) {
      try {
        this.isUploadAdSubmitting = true;

        await api.uploadPremadeAd({
          adFile,
          userId: this.myUserStore.userId,
          programId: this.compiledAd.program_id,
          campaignId: this.compiledAd.campaign_id,
          adId: this.compiledAd.id,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully uploaded ad',
        });

        this.uploadAdDialogIsVisible = false;

        this.getAd();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error uploading ad.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.isUploadAdSubmitting = false;
      }
    },
    async onSubmitPauseAd() {
      try {
        this.isPauseSubmitting = true;

        await api.pauseAd({
          adId: this.compiledAd.id,
          action: 'pause',
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully paused ad',
        });

        this.isPauseDialogVisible = false;

        this.getAd();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error pausing ad.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.isPauseSubmitting = false;
      }
    },
    async onSubmitResumeAd() {
      try {
        this.isResumeSubmitting = true;

        await api.pauseAd({
          adId: this.compiledAd.id,
          action: 'resume',
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully resumed ad',
        });

        this.isResumeDialogVisible = false;

        this.getAd();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error resuming ad.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.isResumeSubmitting = false;
      }
    },
    onExportImpressions({
      impressions,
      interval,
    }) {
      const sanitizedCampaignName = this.compiledAd.campaign_name.replaceAll(' ', '_');
      const filename = `${interval}_impressions_report_${sanitizedCampaignName}`;
      const exportData = interval === CHART_INTERVAL_DAILY
        ? impressions.map((item) => ({
          date: item.timestamp,
          impressions: item.count,
        }))
        : impressions.reduce((acc, {
          count,
          timestamp,
        }) => {
          const previousDaysCount = acc
            .reduce((innerAcc, data) => innerAcc + data.impressions, 0);
          const currentCount = previousDaysCount + count;
          acc.push({
            date: timestamp,
            impressions: currentCount,
          });
          return acc;
        }, []);
      exportToCsv(filename, ['date', 'impressions'], exportData);
    },
    async onSubmitRegenerateAd() {
      try {
        this.isRegenerateAdSubmitting = true;

        await api.createGeneratedAd({
          userId: this.compiledAd.host_id,
          campaignId: this.compiledAd.campaign_id,
          scriptMinutes: 0,
          scriptSeconds: this.compiledAd.ad_length,
          adId: this.compiledAd.id,
          programId: this.compiledAd.program_id,
        });

        const key = generateAdRegenerateAttemptCountKey(this.compiledAd.id);
        const currentAttemptCount = localStorage.getItem(key);

        const newAttemptCount = currentAttemptCount === null
          || Number.isNaN(Number(currentAttemptCount))
          ? 1
          : Number(currentAttemptCount) + 1;

        localStorage.setItem(key, newAttemptCount);

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully regenerated ad. Listen to the new ad for approval.',
        });

        this.regenerateAdDialogIsVisible = false;

        this.getAd();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error regenerating ad.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.isRegenerateAdSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.information-list {
  list-style: none;
  padding: 0;
  display: flex;
  flex-wrap: wrap;

  >li {
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;

    &:last-child {
      margin-right: 0;
    }

    .p-chip {
      margin-right: 0.25rem;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
